import { useRef, useEffect, useState, useCallback, forwardRef, useImperativeHandle } from 'react';

interface SignaturePadProps {
  /** 캔버스 너비 (픽셀). 미지정 시 컨테이너 너비에 맞춤 */
  width?: number;
  /** 캔버스 높이 (픽셀). 기본값 150 */
  height?: number;
  strokeColor?: string;
  strokeWidth?: number;
  backgroundColor?: string;
  onSignatureChange?: (hasSignature: boolean) => void;
  className?: string;
}

// 서명 패드 ref 타입 (외부에서 메서드 호출용)
export interface SignaturePadRef {
  clear: () => void;
  toDataURL: (type?: string, quality?: number) => string;
  isEmpty: () => boolean;
  /** 이미지 파일을 캔버스에 로드 (object-fit: contain). 이미지 로드 후 그리기 비활성화. 지우기(clear)로 초기화 후 그리기 가능 */
  loadImage: (file: File) => Promise<void>;
}

/**
 * 캔버스 기반 서명 패드 컴포넌트 (forwardRef)
 * - 마우스 드래그로 서명 (데스크톱)
 * - 터치로 서명 (모바일/태블릿)
 * - width 미지정 시 컨테이너 너비에 자동 맞춤 (반응형)
 * - useImperativeHandle로 clear/toDataURL/isEmpty/loadImage 메서드 노출
 */
export const SignaturePadWithRef = forwardRef<SignaturePadRef, SignaturePadProps>(
  function SignaturePadWithRef(props, ref) {
    const containerRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const contextRef = useRef<CanvasRenderingContext2D | null>(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [isEmpty, setIsEmpty] = useState(true);
    const [isImageLoaded, setIsImageLoaded] = useState(false);
    const [canvasWidth, setCanvasWidth] = useState(props.width || 400);

    const {
      width,
      height = 150,
      strokeColor = '#000000',
      strokeWidth = 2,
      onSignatureChange,
      className = '',
    } = props;

    // 컨테이너 너비 측정 및 캔버스 크기 조정
    useEffect(() => {
      if (width) {
        setCanvasWidth(width);
        return;
      }

      const container = containerRef.current;
      if (!container) return;

      const updateWidth = () => {
        const containerWidth = container.clientWidth;
        if (containerWidth > 0) {
          setCanvasWidth(containerWidth);
        }
      };

      updateWidth();

      const resizeObserver = new ResizeObserver(updateWidth);
      resizeObserver.observe(container);

      return () => resizeObserver.disconnect();
    }, [width]);

    // 캔버스 초기화
    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const dpr = window.devicePixelRatio || 1;
      canvas.width = canvasWidth * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${canvasWidth}px`;
      canvas.style.height = `${height}px`;

      const context = canvas.getContext('2d');
      if (!context) return;

      context.scale(dpr, dpr);
      context.clearRect(0, 0, canvasWidth, height);
      context.lineCap = 'round';
      context.lineJoin = 'round';
      context.strokeStyle = strokeColor;
      context.lineWidth = strokeWidth;
      contextRef.current = context;
    }, [canvasWidth, height, strokeColor, strokeWidth]);

    const getCoordinates = useCallback((event: MouseEvent | TouchEvent): { x: number; y: number } | null => {
      const canvas = canvasRef.current;
      if (!canvas) return null;

      const rect = canvas.getBoundingClientRect();

      if ('touches' in event) {
        const touch = event.touches[0] || event.changedTouches[0];
        return {
          x: touch.clientX - rect.left,
          y: touch.clientY - rect.top,
        };
      } else {
        return {
          x: event.clientX - rect.left,
          y: event.clientY - rect.top,
        };
      }
    }, []);

    // Note: preventDefault() 제거 - CSS touch-action: none이 스크롤 방지 처리
    const startDrawing = useCallback((event: React.MouseEvent | React.TouchEvent) => {
      if (isImageLoaded) return;
      const coords = getCoordinates(event.nativeEvent);
      if (!coords || !contextRef.current) return;

      contextRef.current.beginPath();
      contextRef.current.moveTo(coords.x, coords.y);
      setIsDrawing(true);
    }, [isImageLoaded, getCoordinates]);

    const draw = useCallback((event: React.MouseEvent | React.TouchEvent) => {
      if (isImageLoaded || !isDrawing || !contextRef.current) return;

      const coords = getCoordinates(event.nativeEvent);
      if (!coords) return;

      contextRef.current.lineTo(coords.x, coords.y);
      contextRef.current.stroke();

      if (isEmpty) {
        setIsEmpty(false);
        onSignatureChange?.(true);
      }
    }, [isImageLoaded, isDrawing, getCoordinates, isEmpty, onSignatureChange]);

    const stopDrawing = useCallback(() => {
      if (contextRef.current) {
        contextRef.current.closePath();
      }
      setIsDrawing(false);
    }, []);

    const clear = useCallback(() => {
      const canvas = canvasRef.current;
      const context = contextRef.current;
      if (!canvas || !context) return;

      context.clearRect(0, 0, canvasWidth, height);
      context.strokeStyle = strokeColor;
      setIsEmpty(true);
      setIsImageLoaded(false);
      onSignatureChange?.(false);
    }, [canvasWidth, height, strokeColor, onSignatureChange]);

    // 표준 해상도(400x150)로 정규화하여 내보내기 (투명 배경)
    // 기기/컨테이너 크기에 관계없이 일관된 서명 이미지 생성
    const toDataURL = useCallback((type: string = 'image/png', quality?: number): string => {
      const canvas = canvasRef.current;
      if (!canvas) return '';

      const EXPORT_WIDTH = 400;
      const EXPORT_HEIGHT = 150;

      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = EXPORT_WIDTH;
      tempCanvas.height = EXPORT_HEIGHT;
      const tempCtx = tempCanvas.getContext('2d');
      if (!tempCtx) return canvas.toDataURL(type, quality);

      tempCtx.clearRect(0, 0, EXPORT_WIDTH, EXPORT_HEIGHT);
      tempCtx.drawImage(canvas, 0, 0, EXPORT_WIDTH, EXPORT_HEIGHT);
      return tempCanvas.toDataURL(type, quality);
    }, []);

    const getIsEmpty = useCallback((): boolean => {
      return isEmpty;
    }, [isEmpty]);

    const loadImage = useCallback((file: File): Promise<void> => {
      return new Promise((resolve, reject) => {
        const canvas = canvasRef.current;
        const context = contextRef.current;
        if (!canvas || !context) {
          reject(new Error('캔버스가 초기화되지 않았습니다'));
          return;
        }

        const displayWidth = canvasWidth;
        const displayHeight = height;

        const img = new Image();
        const objectUrl = URL.createObjectURL(file);

        img.onload = () => {
          URL.revokeObjectURL(objectUrl);

          // object-fit: contain 방식으로 비율 유지하며 캔버스에 맞춤
          const imgRatio = img.width / img.height;
          const canvasRatio = displayWidth / displayHeight;

          let drawWidth: number;
          let drawHeight: number;
          let offsetX: number;
          let offsetY: number;

          if (imgRatio > canvasRatio) {
            drawWidth = displayWidth;
            drawHeight = displayWidth / imgRatio;
            offsetX = 0;
            offsetY = (displayHeight - drawHeight) / 2;
          } else {
            drawHeight = displayHeight;
            drawWidth = displayHeight * imgRatio;
            offsetX = (displayWidth - drawWidth) / 2;
            offsetY = 0;
          }

          // 기존 내용 지우고 이미지 그리기 (DPR 스케일 적용된 컨텍스트 기준)
          context.clearRect(0, 0, displayWidth, displayHeight);
          // context는 이미 scale(dpr, dpr)이 적용되어 있으므로 논리 좌표로 그림
          context.save();
          context.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
          context.restore();

          setIsEmpty(false);
          setIsImageLoaded(true);
          onSignatureChange?.(true);

          resolve();
        };

        img.onerror = () => {
          URL.revokeObjectURL(objectUrl);
          reject(new Error('이미지를 불러오는 데 실패했습니다'));
        };

        img.src = objectUrl;
      });
    }, [canvasWidth, height, onSignatureChange]);

    // ref를 통해 메서드 노출
    useImperativeHandle(ref, () => ({
      clear,
      toDataURL,
      isEmpty: getIsEmpty,
      loadImage,
    }), [clear, toDataURL, getIsEmpty, loadImage]);

    return (
      <div ref={containerRef} className={`signature-pad-container w-full ${className}`}>
        <canvas
          ref={canvasRef}
          className={`border border-ink-300 rounded-control touch-none bg-white ${isImageLoaded ? 'cursor-default' : 'cursor-crosshair'}`}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
          onTouchCancel={stopDrawing}
        />
        <div className="mt-2 flex justify-between items-center">
          <span className="text-xs text-ink-500">
            {isEmpty ? '위 영역에 서명해 주세요' : isImageLoaded ? '이미지가 로드되었습니다' : '서명이 입력되었습니다'}
          </span>
          <button
            type="button"
            onClick={clear}
            className="text-sm text-ink-600 hover:text-ink-900 underline"
          >
            지우기
          </button>
        </div>
      </div>
    );
  }
);
